import {
  parseDeveloperSection,
  type OpenSection,
} from '@/features/developers/developer-sections.js';

/** The management sections, in the order of the tabs. The active one is deep-linked as `?tab=`. */
export type WorkspaceManageTab =
  | 'general'
  | 'start-page'
  | 'access'
  | 'agent'
  | 'knowledge'
  | 'connectors'
  | 'credentials'
  | 'callbacks'
  | 'quality'
  | 'developer'
  | 'code-quality';

export const MANAGE_TABS: readonly WorkspaceManageTab[] = [
  'general',
  'start-page',
  'access',
  'agent',
  'knowledge',
  'connectors',
  'credentials',
  'callbacks',
  'quality',
  'developer',
  'code-quality',
];

export interface WorkspaceManageSearch {
  tab?: WorkspaceManageTab;
  /**
   * A provider whose connect flow should start as soon as the Connectors tab opens.
   *
   * It exists because connecting is not a one-click action for every provider — SharePoint asks for a
   * name, sometimes whose directory it is, sometimes what it may do — and that whole flow lives on the
   * connector card. A surface elsewhere that offers "connect another" therefore sends the person to
   * the card rather than growing a second copy of the flow, and this says which card to start.
   *
   * It is consumed once and stripped from the URL, so a reload or a Back does not connect again.
   */
  connect?: string;
  /** The open section of the Developer settings form, so a reload opens the same one. */
  section?: OpenSection;
}

/** A provider key in the plain shape the connector cards use. */
const CONNECT_PATTERN = /^[a-z0-9_-]{1,40}$/;

/**
 * Reads the query of the manage page: a known tab, a provider to connect and a Developer settings
 * section, nothing else.
 */
export function parseWorkspaceManageSearch(
  raw: Record<string, unknown>,
): WorkspaceManageSearch {
  return {
    tab: MANAGE_TABS.includes(raw.tab as WorkspaceManageTab)
      ? (raw.tab as WorkspaceManageTab)
      : undefined,
    connect:
      typeof raw.connect === 'string' && CONNECT_PATTERN.test(raw.connect)
        ? raw.connect
        : undefined,
    section: parseDeveloperSection(raw.section),
  };
}
